import * as utils from "common/utils";
import Alert from "components/Alert";
import { HelpTooltip } from "components/Help";
import ProjectedResultsWidget from "../components/ProjectedResultsWidget";
import ProjectedBlocksResults from "../components/ProjectedBlocksResults";
import ProjectedFodderCropRotationsResults from "../components/ProjectedFodderCropRotationsResults";
import ProjectedGrassFedStandardResults from "../components/ProjectedGrassFedStandardResults";
import { GhgEmissions } from "containers/BudgetHome/Overview/Ghg";
import { useOverseerModelRun } from "containers/BudgetHome";

export default function RedMeatPlatform({ analysis }) {
    const { isLoading, data: redMeatPlatformResults } = useRedMeatPlatformResults(analysis);

    if (isLoading)
        return null;

    const hasResults = redMeatPlatformResults?.farm?.nitrogen !== undefined;
    if (!hasResults) {
        return <Alert type="error" text="There are no red meat platform results for this analysis" />
    }

    const fullSystemIsTheRedMeatPlatform = redMeatPlatformResults?.farm?.nutrientBudget !== undefined;
    if (fullSystemIsTheRedMeatPlatform) {
        return <Alert type="warning" text="This analysis only models the red meat platform areas of the farm. You will need to go back and enter the non-meat producing areas (if any) in order to see how the red meat platform compares to the full farm system." />
    }

    return (
        <>
            <Alert type="info" text="The red meat platform represents the areas of the farm used for producing red meat, specifically - the land designated for grazing and supporting red meat producing animals. By isolating results to the red meat platform, this report aims to provide a clearer understanding of the environmental impacts directly associated with red meat production, without the influence of other non-meat producing areas such as support blocks or runoff land." />
            <div className="HeroPanel u-mt-md u-page-break u-print-border">
                <div className="HeroPanel-title">Red meat platform</div>
                <div className="HeroValue u-pt-lg u-flexWrap">
                    <div className="HeroValue-item u-mb-lg">
                        <h5>
                            Area
                            <HelpTooltip tip={redMeatPlatformResults.farm.farmArea.description} />
                        </h5>
                        <div className="u-flex u-flexColumn">
                            <span id="red_meat_platform_ghg" className="h1">
                                {utils.numeric.format(redMeatPlatformResults.farm.farmArea.value, 1)} <small>{redMeatPlatformResults.farm.farmArea.unit.notation}</small>
                            </span>
                        </div>
                        <div className="u-flex u-flexColumn">
                            <span id="red_meat_platform_ghgha" className="h2">
                                {utils.numeric.format(redMeatPlatformResults.farm.percentageOfTotalFarmArea.value, 1)} <small>{redMeatPlatformResults.farm.percentageOfTotalFarmArea.unit.notation} of total farm area</small>
                            </span>
                        </div>
                    </div>
                    <ProjectedGrassFedStandardResults platformResults={redMeatPlatformResults?.farm} />
                </div>
                <ProjectedResultsWidget title="Red meat platform" platformResults={redMeatPlatformResults?.farm} />
            </div>
            <ProjectedBlocksResults blocksResults={redMeatPlatformResults?.blocks} />
            <ProjectedFodderCropRotationsResults fodderCropRotationsResults={redMeatPlatformResults?.fodderCropRotations} />
            <br />
            <GhgEmissions ghgResults={redMeatPlatformResults?.farm?.ghg} title="GHG emissions for the red meat platform" />
        </>
    );
}

function useRedMeatPlatformResults(analysis) {
    const { isLoading, data } = useOverseerModelRun(analysis, "RedMeatPlatform");

    return {
        isLoading,
        data: data?.modelResults,
    }
}
